import { useState } from "react";
import { useGetPersonnummerStatus } from "api/personnummer/useGetPersonnummerStatus";
import { Button } from "components/shadcnUI/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "components/shadcnUI/Dialog";
import { PersonnummerForm } from "./PersonnummerForm";

type View = "form" | "later" | "thanks" | "done";

/**
 * Pop-upen som samlar in personnummer inför bytet till BankID (ADE-37).
 *
 * Visas för varje inloggad användare som inte har skickat in något, tills
 * API:t i rapportgeneratorn (ADE-50) säger att det finns en rad. Går statusen
 * inte att hämta visas ingenting; nästa inloggning försöker igen.
 *
 * Bara "Senare" stänger pop-upen, och då står en banner kvar som inte går att
 * stänga. Esc och klick utanför gör ingenting.
 *
 * Tillfällig: tas bort när bytet till BankID är gjort (epic ADE-46).
 */
export const PersonnummerPrompt = () => {
  const { hasPersonnummer } = useGetPersonnummerStatus();
  const [view, setView] = useState<View>("form");

  // Efter inskick säger API:t att det finns en rad, men tacket ska stå kvar
  // tills användaren stänger det.
  if (view === "done") return null;
  if (view !== "thanks" && hasPersonnummer !== false) return null;

  return (
    <>
      {view === "later" && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-secondary-100 px-4 py-2 text-sm text-white-100"
        >
          <span>Fyll i ditt personnummer inför bytet till BankID.</span>
          <button
            type="button"
            className="font-semibold underline underline-offset-2 hover:no-underline"
            onClick={() => setView("form")}
          >
            Fyll i nu
          </button>
        </div>
      )}
      <Dialog open={view === "form" || view === "thanks"}>
        <DialogContent
          className="max-h-[90vh] max-w-2xl overflow-y-auto"
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <img
            src={`${process.env.PUBLIC_URL}/logo_large.svg`}
            alt="Adecla"
            className="mx-auto mb-4 h-16"
          />
          <DialogHeader>
            <DialogTitle>Snart loggar du in med BankID</DialogTitle>
            <DialogDescription>
              Vi byter inloggning till kundportalen. Snart loggar du in med
              BankID i stället för med lösenord. För att koppla BankID till ditt
              konto behöver vi ditt personnummer.
            </DialogDescription>
          </DialogHeader>
          {view === "thanks" ? (
            <div className="flex flex-col gap-6">
              <p role="status">
                Tack! Vi har tagit emot uppgifterna. Du behöver inte göra något
                mer just nu.
              </p>
              <Button
                className="self-end bg-secondary-100 text-white-100 hover:bg-secondary-90"
                onClick={() => setView("done")}
              >
                Stäng
              </Button>
            </div>
          ) : (
            <PersonnummerForm
              onLater={() => setView("later")}
              onSubmitted={() => setView("thanks")}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
